import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Loader2, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { GdprConsent } from '../components/GdprConsent';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Translations
const translations = {
  ca: {
    title: "Com va ser la seva experiència a UGOLF Aravell Andorra?",
    subtitle: "La seva opinió ens ajuda a millorar",
    visitDate: "Data de la visita",
    visitDateRequired: "(obligatori)",
    comment: "Pot deixar un comentari",
    commentPlaceholder: "Escrigui aquí els seus comentaris...",
    optionalFields: "Dades opcionals",
    name: "Nom",
    phone: "Telèfon",
    email: "Correu electrònic",
    emailNote: "Si deixa el seu email rebrà una confirmació",
    submit: "Enviar valoració",
    submitting: "Enviant...",
    thankYou: "Gràcies per la seva valoració!",
    thankYouMessage: "La seva opinió ens importa molt. A la seva pròxima visita pot mencionar aquesta valoració per poder tenir un detall especial amb vostè.",
    emailSent: "Li hem enviat un email de confirmació.",
    error: "Hi ha hagut un error. Si us plau, intenti-ho de nou.",
    required: "Si us plau, valori totes les categories i indiqui la data de visita.",
    selectLanguage: "Idioma"
  },
  es: {
    title: "¿Cómo fue su experiencia en UGOLF Aravell Andorra?",
    subtitle: "Su opinión nos ayuda a mejorar",
    visitDate: "Fecha de la visita",
    visitDateRequired: "(obligatorio)",
    comment: "Puede dejar un comentario",
    commentPlaceholder: "Escriba aquí sus comentarios...",
    optionalFields: "Datos opcionales",
    name: "Nombre",
    phone: "Teléfono",
    email: "Correo electrónico",
    emailNote: "Si deja su email recibirá una confirmación",
    submit: "Enviar valoración",
    submitting: "Enviando...",
    thankYou: "¡Gracias por su valoración!",
    thankYouMessage: "Su opinión nos importa mucho. En su próxima visita puede mencionar esta valoración para poder tener un detalle especial con Vd.",
    emailSent: "Le hemos enviado un email de confirmación.",
    error: "Ha habido un error. Por favor, inténtelo de nuevo.",
    required: "Por favor, valore todas las categorías e indique la fecha de visita.",
    selectLanguage: "Idioma"
  },
  fr: {
    title: "Comment s'est passée votre expérience à UGOLF Aravell Andorra?",
    subtitle: "Votre avis nous aide à nous améliorer",
    visitDate: "Date de la visite",
    visitDateRequired: "(obligatoire)",
    comment: "Vous pouvez laisser un commentaire",
    commentPlaceholder: "Écrivez vos commentaires ici...",
    optionalFields: "Données optionnelles",
    name: "Nom",
    phone: "Téléphone",
    email: "E-mail",
    emailNote: "Si vous laissez votre email, vous recevrez une confirmation",
    submit: "Envoyer l'évaluation",
    submitting: "Envoi en cours...",
    thankYou: "Merci pour votre évaluation!",
    thankYouMessage: "Votre avis compte beaucoup pour nous. Lors de votre prochaine visite, vous pouvez mentionner cette évaluation pour recevoir une attention spéciale.",
    emailSent: "Nous vous avons envoyé un email de confirmation.",
    error: "Une erreur s'est produite. Veuillez réessayer.",
    required: "Veuillez évaluer toutes les catégories et indiquer la date de visite.",
    selectLanguage: "Langue"
  },
  en: {
    title: "How was your experience at UGOLF Aravell Andorra?",
    subtitle: "Your opinion helps us improve",
    visitDate: "Visit date",
    visitDateRequired: "(required)",
    comment: "You can leave a comment",
    commentPlaceholder: "Write your comments here...",
    optionalFields: "Optional information",
    name: "Name",
    phone: "Phone",
    email: "Email",
    emailNote: "If you leave your email you will receive a confirmation",
    submit: "Submit review",
    submitting: "Submitting...",
    thankYou: "Thank you for your feedback!",
    thankYouMessage: "Your opinion matters a lot to us. On your next visit, you can mention this review to receive a special detail with you.",
    emailSent: "We have sent you a confirmation email.",
    error: "An error occurred. Please try again.",
    required: "Please rate all categories and indicate the visit date.",
    selectLanguage: "Language"
  }
};

// Smiley ratings component
const SmileyRating = ({ value, onChange }) => {
  const smileys = [
    { value: 1, emoji: '😞', label: 'Muy mal' },
    { value: 2, emoji: '😕', label: 'Mal' },
    { value: 3, emoji: '😐', label: 'Regular' },
    { value: 4, emoji: '🙂', label: 'Bien' },
    { value: 5, emoji: '😊', label: 'Excelente' }
  ];

  return (
    <div className="flex gap-2 justify-center">
      {smileys.map((smiley) => (
        <button
          key={smiley.value}
          type="button"
          onClick={() => onChange(smiley.value)}
          className={`text-3xl md:text-4xl p-2 rounded-full transition-all ${
            value === smiley.value 
              ? 'bg-[#1A4D2E]/10 scale-125 ring-2 ring-[#1A4D2E]' 
              : 'hover:bg-gray-100 opacity-50 hover:opacity-100'
          }`}
          title={smiley.label}
        >
          {smiley.emoji}
        </button>
      ))}
    </div>
  );
};

const SurveyPage = () => {
  const { language, setLanguage } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [ratings, setRatings] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [formData, setFormData] = useState({
    visitDate: '',
    comment: '',
    name: '',
    phone: '',
    email: ''
  });

  const t = translations[language] || translations.ca;

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/survey/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
        // Initialize ratings
        const initialRatings = {};
        data.forEach(cat => {
          initialRatings[cat.id] = 0;
        });
        setRatings(initialRatings);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (cat) => {
    return cat[`name_${language}`] || cat.name_es;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const allRated = Object.values(ratings).every(r => r > 0);
    if (!allRated || !formData.visitDate) {
      toast.error(t.required);
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/survey/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ratings: Object.entries(ratings).map(([category_id, rating]) => ({
            category_id,
            rating
          })),
          comment: formData.comment,
          visit_date: formData.visitDate,
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          language
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSubmitted(true);
        setEmailSent(data.email_sent);
      } else {
        toast.error(t.error);
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      toast.error(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </main>
    );
  }

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#F9F9F7] py-12" data-testid="survey-success">
        <div className="container-custom max-w-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-lg p-8 text-center"
          >
            <div className="w-20 h-20 bg-[#1A4D2E] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-display font-bold text-[#1A4D2E] mb-4">
              {t.thankYou}
            </h1>
            <p className="text-[#666666] mb-4">
              {t.thankYouMessage}
            </p>
            {emailSent && (
              <p className="text-[#1A4D2E] font-medium">
                {t.emailSent}
              </p>
            )}
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F9F9F7] py-8 md:py-12" data-testid="survey-page">
      <div className="container-custom max-w-2xl">
        {/* Language Selector */}
        <div className="flex justify-end mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#666666]">{t.selectLanguage}:</span>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="px-3 py-1.5 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
            >
              <option value="ca">Català</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#1A4D2E] px-6 py-8 text-center">
            {/* Language Selector inside header */}
            <div className="flex justify-center mb-6">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="px-4 py-2 text-sm bg-white/10 text-white border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer"
              >
                <option value="ca" className="text-[#333333]">🇦🇩 Català</option>
                <option value="es" className="text-[#333333]">🇪🇸 Español</option>
                <option value="fr" className="text-[#333333]">🇫🇷 Français</option>
                <option value="en" className="text-[#333333]">🇬🇧 English</option>
              </select>
            </div>
            
            <h1 className="text-xl md:text-2xl font-display font-bold text-white mb-2">
              {t.title}
            </h1>
            <p className="text-white/80 text-sm">
              {t.subtitle}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Categories */}
            <div className="space-y-6">
              {categories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#F9F9F7] rounded-lg p-4"
                >
                  <h3 className="text-center font-medium text-[#333333] mb-3">
                    {getCategoryName(category)}
                  </h3>
                  <SmileyRating
                    value={ratings[category.id]}
                    onChange={(value) => setRatings(prev => ({
                      ...prev,
                      [category.id]: value
                    }))}
                  />
                </motion.div>
              ))}
            </div>

            {/* Visit Date */}
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                {t.visitDate} <span className="text-[#D4AF37]">{t.visitDateRequired}</span>
              </label>
              <input
                type="date"
                value={formData.visitDate}
                onChange={(e) => setFormData(prev => ({ ...prev, visitDate: e.target.value }))}
                required
                className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E]"
              />
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                {t.comment}
              </label>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                placeholder={t.commentPlaceholder}
                rows={4}
                className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] resize-none"
              />
            </div>

            {/* Optional Fields */}
            <div className="border-t border-[#E5E5E5] pt-6">
              <h3 className="text-sm font-medium text-[#666666] mb-4">
                {t.optionalFields}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#888888] mb-1">{t.name}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#888888] mb-1">{t.phone}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-[#888888] mb-1">{t.email}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                  />
                  <p className="text-xs text-[#888888] mt-1">{t.emailNote}</p>
                </div>
              </div>
            </div>

            <GdprConsent
              privacy={gdprAccepted}
              onPrivacyChange={setGdprAccepted}
              idPrefix="survey"
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.submitting}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {t.submit}
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </main>
  );
};

export default SurveyPage;
